//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SamplingCampaignParams } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsTypes';
import {
  SamplingCampaignRequestSectionName,
  getSamplingCampaignSectionDisplayName,
} from '@campaigns/constants/sampling/SamplingCampaignRequestSectionConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { PrimaryRouteAnchor } from '@components/RouteAnchor';
import { BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { useCurrentStateAndParams } from '@hooks/useCurrentStateAndParams';

import { RoutableStateName } from 'app/top-level-states';

export const SamplingRequestSubmitErrorsModal = () => {
  const { params, state } = useCurrentStateAndParams<SamplingCampaignParams>();
  const isPrepaidFlow = state.startsWith('campaigns.sampling-prepaid-');
  const {
    closeSubmitErrorsModal,
    isSubmitErrorsModalOpen,
    samplingCampaignRequest,
    validationSubmitErrors,
  } = useSamplingCampaign();

  return (
    <Modal
      closeModal={closeSubmitErrorsModal}
      contents={
        <div>
          <BodyText>
            We found issues in the following sections, please fix these before
            continuing
          </BodyText>
          <ErrorsSection>
            {Array.from(validationSubmitErrors.entries()).map(
              ([section, errors]: [
                SamplingCampaignRequestSectionName,
                string[],
              ]) => {
                let sectionState: RoutableStateName = `campaigns.sampling-campaign-request-details-edit-${section}`;
                if (isPrepaidFlow) {
                  const prepaidSection =
                    section ===
                    SamplingCampaignRequestSectionName.PURCHASE_OPTIONS
                      ? SamplingCampaignRequestSectionName.SUMMARY
                      : section;
                  sectionState = `campaigns.sampling-prepaid-campaign-request-details-edit-${prepaidSection}`;
                }

                return (
                  <div key={section}>
                    <PrimaryRouteAnchor
                      state={sectionState}
                      stateParams={{ campaignId: params.campaignId }}
                    >
                      {getSamplingCampaignSectionDisplayName(
                        section,
                        samplingCampaignRequest?.fulfillmentOptions
                          ?.fulfillmentType,
                      )}
                    </PrimaryRouteAnchor>
                    <StyledList>
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </StyledList>
                  </div>
                );
              },
            )}
          </ErrorsSection>
        </div>
      }
      isFooterEnabled
      isModalOpen={isSubmitErrorsModalOpen}
      primaryButtonInfo={{
        buttonContent: 'Close',
        onClick: closeSubmitErrorsModal,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_SUBMIT_ERRORS_MODAL_CLOSE_BUTTON,
      }}
      shouldIncludeSecondaryButton={false}
      shouldIncludeTertiaryButton={false}
      title='Something isn’t quite right'
      width='wide'
    />
  );
};

const ErrorsSection = styled.div`
  display: flex;
  margin-top: ${({ theme }) => theme.spacings[4]};
  gap: ${({ theme }) => theme.spacings[4]};
  flex-wrap: wrap;
`;

const StyledList = styled.ul`
  list-style-type: none;
  padding: 0;
`;
